import { AlertCircle, CheckCircle2, ClipboardList, Home, Truck } from 'lucide-react';
import {
  BooleanToggle,
  ChoiceConsequences,
  Field,
  SectionCard,
  inputBase,
} from './FormPromptControls.tsx';
import type { AIConfigurationStepId } from '../../configuration/steps.ts';

type FreightRow = Record<string, unknown>;

type FreteMeta = {
  tooltip?: string;
  helper?: string;
  rowTooltip?: string;
  firstFieldLabel?: string;
  firstFieldPlaceholder?: string;
} | null;

type PedidoChecklistItem = {
  label: string;
  ok: boolean;
  step: AIConfigurationStepId;
};

type FulfillmentStepProps = {
  fulfillmentForcado: boolean;
  form: any;
  errors: Record<string, string>;
  entregaObrigatoria: boolean;
  retiradaObrigatoria: boolean;
  showEntrega: boolean;
  showRetirada: boolean;
  modoPedidos: 'whatsapp' | 'link' | null;
  freteMeta: FreteMeta;
  freteHasAreaField: boolean;
  freteHasKmField: boolean;
  freteHasTempoField: boolean;
  freteRowGridClass: string;
  deliveryPreviewText: string;
  pedidoChecklist: PedidoChecklistItem[];
  updateField: (key: string, value: unknown) => void;
  updateFulfillmentAvailability: (field: string, typeLabel: string, value: boolean) => void;
  updateModeloFrete: (model: string) => void;
  updateFreteRow: (index: number, field: string, value: string) => void;
  addFreteRow: () => void;
  removeFreteRow: (index: number) => void;
  formatFreteCurrency: (index: number) => void;
  sortFreteRadiusRules: () => void;
  readFreteArea: (row: FreightRow) => string;
  readFreteKm: (row: FreightRow) => string;
  readFreteTempo: (row: FreightRow) => string;
  readFretePreco: (row: FreightRow) => string;
  parseFreteNumber: (value: string) => number | null;
  handleReviewEdit: (step: AIConfigurationStepId) => void;
};

const FulfillmentStep = ({
  fulfillmentForcado,
  form,
  errors,
  entregaObrigatoria,
  retiradaObrigatoria,
  showEntrega,
  showRetirada,
  modoPedidos,
  freteMeta,
  freteHasAreaField,
  freteHasKmField,
  freteHasTempoField,
  freteRowGridClass,
  deliveryPreviewText,
  pedidoChecklist,
  updateField,
  updateFulfillmentAvailability,
  updateModeloFrete,
  updateFreteRow,
  addFreteRow,
  removeFreteRow,
  formatFreteCurrency,
  sortFreteRadiusRules,
  readFreteArea,
  readFreteKm,
  readFreteTempo,
  readFretePreco,
  parseFreteNumber,
  handleReviewEdit,
}: FulfillmentStepProps) => (
  <>
{fulfillmentForcado && (
  <div className="rounded-xl border border-teal-200 bg-teal-50 px-4 py-3 text-sm font-medium text-teal-900">
    Esta etapa é obrigatória porque a IA registra pedidos pelo WhatsApp: ative entrega, retirada ou ambos para o pedido chegar ao cliente.
  </div>
)}
{(
  <SectionCard
    title="Entrega"
    icon={Truck}
    description="Configure tempo e taxa para a IA calcular e orientar o cliente."
  >
    <div className="grid gap-4 md:grid-cols-2">
      <Field
        label="Oferece entrega?"
        required={entregaObrigatoria}
        error={errors.entrega_ativa}
        errorKey="entrega_ativa"
      >
        <div className="space-y-3">
          <BooleanToggle
            value={form.entrega_ativa}
            disableNo={entregaObrigatoria}
            onChange={(value) => updateFulfillmentAvailability('entrega_ativa', 'pedidos delivery', value)}
          />
          {entregaObrigatoria && (
            <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs leading-5 text-amber-900">
              A opção “Não” está bloqueada porque pedidos com entrega fazem parte dos recursos ativos. Para desativar a entrega, altere os recursos em “O que a IA faz”.
            </p>
          )}
        </div>
      </Field>
      {showEntrega && (
        <Field
          label="Tempo estimado de entrega (mín. a máx.)"
          error={errors.tempo_estimado_entrega_min || errors.tempo_estimado_entrega_max}
          errorKey="tempo_estimado_entrega_min"
          helper="Ex.: de 30 a 50"
          tooltip="Use um intervalo realista para reduzir reclamações."
        >
          <div className="grid grid-cols-2 gap-2">
            <input
              type="number"
              min="0"
              aria-label="Tempo mínimo estimado de entrega"
              className={inputBase}
              placeholder="Ex.: 30"
              value={form.tempo_estimado_entrega_min}
              onChange={(e) => updateField('tempo_estimado_entrega_min', e.target.value)}
            />
            <input
              type="number"
              min="0"
              aria-label="Tempo máximo estimado de entrega"
              className={inputBase}
              placeholder="Ex.: 50"
              value={form.tempo_estimado_entrega_max}
              onChange={(e) => updateField('tempo_estimado_entrega_max', e.target.value)}
            />
          </div>
        </Field>
      )}
    </div>
    {showEntrega && (
    <>
    <Field
      label="Como calcula a taxa de entrega"
      error={errors.modelo_frete}
      errorKey="modelo_frete"
      tooltip={
        freteMeta?.tooltip ||
        "Escolha o modelo que você realmente usa (bairro, raio ou taxa fixa)."
      }
    >
      <select
        className={inputBase}
        value={form.modelo_frete}
        onChange={(e) => updateModeloFrete(e.target.value)}
      >
        <option value="">Selecione</option>
        <option value="bairros_cep">Por bairro</option>
        <option value="raio">Por raio (km)</option>
        <option value="fixa">Taxa fixa</option>
      </select>
      {freteMeta?.helper && (
        <p className="mt-2 text-xs text-slate-500">{freteMeta.helper}</p>
      )}
    </Field>
    {form.modelo_frete && (
      <Field
        label="Regras de taxa"
        error={errors.tabela_frete}
        errorKey="tabela_frete"
        tooltip={freteMeta?.rowTooltip || 'Preencha as regras com os valores reais de operação.'}
      >
        <div className="space-y-3">
          {form.tabela_frete.length === 0 && (
            <p className="text-xs text-slate-400">
              Nenhuma regra de frete cadastrada.
            </p>
          )}
          {form.tabela_frete.length > 0 && (
            <div
              className={`hidden text-[11px] font-semibold uppercase tracking-wide text-slate-400 md:grid ${freteRowGridClass}`}
            >
              {freteHasAreaField && <span>Bairro</span>}
              {freteHasKmField && <span>Raio (km)</span>}
              {freteHasTempoField && <span>Tempo estimado (min)</span>}
              <span>Valor da taxa (R$)</span>
              <span></span>
            </div>
          )}
          {form.tabela_frete.map((row, index) => (
            <div
              key={`frete-${index}`}
              className={freteRowGridClass}
            >
              {freteHasAreaField && (
                <div className="space-y-1">
                  <span className="text-[11px] text-slate-400 md:hidden">
                    {freteMeta?.firstFieldLabel || 'Bairro'}
                  </span>
                  <input
                    className={inputBase}
                    aria-label={`Bairro da regra de entrega ${index + 1}`}
                    placeholder={freteMeta?.firstFieldPlaceholder || 'Ex.: Centro'}
                    value={readFreteArea(row)}
                    onChange={(e) => updateFreteRow(index, 'area', e.target.value)}
                  />
                </div>
              )}
              {freteHasKmField && (
                <div className="space-y-1">
                  <span className="text-[11px] text-slate-400 md:hidden">
                    {freteMeta?.firstFieldLabel || 'Raio (km)'}
                  </span>
                  <input
                    className={inputBase}
                    aria-label={`Distância máxima da regra de entrega ${index + 1}`}
                    placeholder={freteMeta?.firstFieldPlaceholder || 'Ex.: 5'}
                    value={readFreteKm(row)}
                    onChange={(e) => updateFreteRow(index, 'km', e.target.value)}
                    onBlur={sortFreteRadiusRules}
                  />
                </div>
              )}
              {freteHasTempoField && (
                <div className="space-y-1">
                  <span className="text-[11px] text-slate-400 md:hidden">
                    Tempo estimado (min)
                  </span>
                  <input
                    className={inputBase}
                    aria-label={`Tempo da regra de entrega ${index + 1}`}
                    placeholder="Ex.: 35"
                    value={readFreteTempo(row)}
                    onChange={(e) => updateFreteRow(index, 'tempo', e.target.value)}
                  />
                </div>
              )}
              <div className="space-y-1">
                <span className="text-[11px] text-slate-400 md:hidden">
                  Valor da taxa (R$)
                </span>
                  <input
                    className={inputBase}
                    inputMode="decimal"
                    aria-label={`Valor da regra de entrega ${index + 1}`}
                    placeholder="Ex.: 8,50"
                    value={readFretePreco(row)}
                    onChange={(e) => updateFreteRow(index, 'preco', e.target.value)}
                    onBlur={() => formatFreteCurrency(index)}
                  />
              </div>
              <button
                type="button"
                onClick={() => removeFreteRow(index)}
                className="rounded-lg border border-slate-200 bg-white px-3 text-xs text-slate-600 hover:border-rose-300 hover:text-rose-600"
              >
                Remover
              </button>
            </div>
          ))}
          {form.modelo_frete === 'raio' && form.tabela_frete.some((row) => parseFreteNumber(readFreteKm(row)) !== null) && (
            <div className="space-y-1 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2" aria-label="Resumo das faixas de entrega">
              {[...form.tabela_frete]
                .sort((left, right) => (parseFreteNumber(readFreteKm(left)) || 0) - (parseFreteNumber(readFreteKm(right)) || 0))
                .map((row, index) => {
                  const km = parseFreteNumber(readFreteKm(row));
                  const price = parseFreteNumber(readFretePreco(row));
                  if (km === null || price === null) return null;
                  return (
                    <p key={`${km}-${index}`} className="text-xs font-medium text-slate-700">
                      Até {km.toLocaleString('pt-BR')} km: {price.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                    </p>
                  );
                })}
            </div>
          )}
          <button
            type="button"
            onClick={addFreteRow}
            className="text-xs font-semibold text-teal-700 hover:text-teal-800"
          >
            {form.modelo_frete === 'bairros_cep'
              ? '+ Adicionar bairro'
              : form.modelo_frete === 'raio'
                ? '+ Adicionar faixa de raio'
                : '+ Adicionar regra de taxa fixa'}
          </button>
        </div>
      </Field>
    )}
    <div className="grid gap-4 md:grid-cols-2">
      <Field
        label="Informar a taxa automaticamente"
        errorKey="informar_taxa_automaticamente"
      >
        <div className="space-y-3">
          <BooleanToggle
            value={form.informar_taxa_automaticamente}
            onChange={(value) => updateField('informar_taxa_automaticamente', value)}
          />
          <ChoiceConsequences
            yes="assim que conseguir calcular a entrega, a IA informa a taxa ao cliente sem ele precisar perguntar."
            no="a IA calcula e inclui a taxa no pedido, mas só apresenta o valor quando o cliente perguntar ou na confirmação final."
          />
        </div>
      </Field>
      <Field
        label="Exigir o endereço completo antes de calcular a taxa?"
        errorKey="pedir_endereco_antes_estimativa"
      >
        <div className="space-y-3">
          <BooleanToggle
            value={form.pedir_endereco_antes_estimativa}
            onChange={(value) => updateField('pedir_endereco_antes_estimativa', value)}
          />
          <ChoiceConsequences
            yes="antes de calcular, a IA solicita rua, número, bairro, cidade, estado e CEP. Isso evita informar uma taxa para a localização errada."
            no="a IA pode dar uma estimativa com a região ou distância já informada, mas ainda pede o endereço completo antes de confirmar o pedido."
          />
        </div>
      </Field>
    </div>
    <div className="grid gap-4 md:grid-cols-2">
      <Field
        label="Se não entregar, oferecer retirada"
        errorKey="oferecer_retirada_fora_area"
        tooltip="Ajuda a não perder venda quando o cliente está fora da área."
      >
        <BooleanToggle
          value={form.oferecer_retirada_fora_area}
          onChange={(value) => updateField('oferecer_retirada_fora_area', value)}
        />
      </Field>
      <Field
        label="Mensagem fora da área"
        errorKey="regra_fora_da_area"
        tooltip="Mensagem que a IA envia quando não entrega naquela região."
      >
        <input
          className={inputBase}
          value={form.regra_fora_da_area}
          onChange={(e) => updateField('regra_fora_da_area', e.target.value)}
          placeholder="Ex.: Não entregamos no seu endereço, mas você pode retirar no balcão."
        />
      </Field>
    </div>
    <div className="rounded-xl border border-emerald-100 bg-emerald-50/70 p-4" aria-live="polite">
      <p className="text-xs font-bold uppercase tracking-wide text-emerald-700">Prévia da resposta</p>
      <p className="mt-2 text-sm text-slate-700">{deliveryPreviewText}</p>
    </div>
    </>
    )}
  </SectionCard>
)}

{(
  <SectionCard
    title="Retirada"
    icon={Home}
    description="Configure tempo e endereço para o cliente retirar."
  >
    <div className="grid gap-4 md:grid-cols-2">
      <Field
        label="Oferece retirada no local?"
        required={retiradaObrigatoria}
        error={errors.retirada_ativa}
        errorKey="retirada_ativa"
      >
        <div className="space-y-3">
          <BooleanToggle
            value={form.retirada_ativa}
            disableNo={retiradaObrigatoria}
            onChange={(value) => updateFulfillmentAvailability('retirada_ativa', 'pedidos retirada', value)}
          />
          {retiradaObrigatoria && (
            <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs leading-5 text-amber-900">
              A opção “Não” está bloqueada porque pedidos para retirada fazem parte dos recursos ativos. Para desativar a retirada, altere os recursos em “O que a IA faz”.
            </p>
          )}
        </div>
      </Field>
      {showRetirada && (
        <Field
          label="Tempo para retirada ficar pronto (mín. a máx.)"
          error={errors.tempo_estimado_retirada_min || errors.tempo_estimado_retirada_max}
          errorKey="tempo_estimado_retirada_min"
          helper="Ex.: de 20 a 35"
          tooltip="Tempo médio para o pedido ficar pronto."
        >
          <div className="grid grid-cols-2 gap-2">
            <input
              type="number"
              min="0"
              aria-label="Tempo mínimo para retirada"
              className={inputBase}
              placeholder="Ex.: 20"
              value={form.tempo_estimado_retirada_min}
              onChange={(e) => updateField('tempo_estimado_retirada_min', e.target.value)}
            />
            <input
              type="number"
              min="0"
              aria-label="Tempo máximo para retirada"
              className={inputBase}
              placeholder="Ex.: 35"
              value={form.tempo_estimado_retirada_max}
              onChange={(e) => updateField('tempo_estimado_retirada_max', e.target.value)}
            />
          </div>
        </Field>
      )}
    </div>
  </SectionCard>
)}

{modoPedidos === 'whatsapp' && (
  <SectionCard
    title="Checklist do pedido"
    icon={ClipboardList}
    description="Tudo que a IA precisa para registrar pedidos pelo WhatsApp."
  >
    <ul className="space-y-2">
      {pedidoChecklist.map((item) => (
        <li
          key={item.label}
          className={`flex flex-wrap items-center justify-between gap-3 rounded-lg border px-3 py-2 ${
            item.ok ? 'border-emerald-200 bg-emerald-50/60' : 'border-amber-200 bg-amber-50/60'
          }`}
        >
          <span className={`flex items-center gap-2 text-sm font-medium ${item.ok ? 'text-emerald-800' : 'text-amber-900'}`}>
            {item.ok
              ? <CheckCircle2 size={16} className="text-emerald-600" />
              : <AlertCircle size={16} className="text-amber-600" />}
            {item.label}
          </span>
          {!item.ok && (
            <button type="button" className="ai-button is-secondary" onClick={() => handleReviewEdit(item.step)}>
              Configurar
            </button>
          )}
        </li>
      ))}
    </ul>
  </SectionCard>
)}

  </>
);

export default FulfillmentStep;
